<template>
    <CqModal
        fullscreen
        forceFitToScreen
        class="workScheduleModal"
        :showModal="show"
        @closeModal="closeModal"
        :heading="heading"
        maxHeight="calc(100vh - 2rem)"
    >
        <template #header v-if="hasActiveDay">
            <div class="extraHeaderContent">
                <CqButton
                    :label="automaticAssignLabel"
                    :disabled="isAutomaticAssigning"
                    @click="assignAutomatically"
                    margin="none"
                />
                <CqButton
                    :label="removeAssignedLabel"
                    :disabled="isRemovingAssignments || !hasWorkScheduleAssignmentsForActiveDay"
                    @click="openDeleteModal"
                    dangerous
                    margin="none"
                />
                <DisplayOptionsMenu v-model="workSchedulePreferences" :hasFilterablePositionTypes="hasFilterablePositionTypes" />
            </div>
        </template>
        <div class="content" ref="content" :style="cssCustomProperties">
            <div class="workSchedulePositionsHolder" :style="{'--work-schedules-height': workSchedulesHeightAsString}" ref="workSchedulePositionsHolder">
                <div class="workScheduleGridContainer" v-if="hasActiveDay">
                    <TimeBar :renderedDays="renderedDays" :activeDay="activeDay!.date" />
                    <WorkScheduleGroupLabels
                        v-if="Object.keys(groupLabelsWithPositionsInVisibleDateRange).length > 0"
                        :groupLabels="groupLabelsWithPositionsInVisibleDateRange"
                        :renderedDateRange="renderedDateRange"
                        :renderedDays="renderedDays"
                        @dragStart:workSchedulePosition="dragStartWorkSchedulePosition"
                        @dragEnd:workSchedulePosition="clearNotDroppableDeploymentsAndSpecifications"
                    />
                    <FullyAssigned v-else />
                </div>
            </div>
            <BoardDivider @moveDivider="moveDivider" />
            <div class="deploymentsHolder" @scroll="scrollHorizontal" ref="deploymentsHolder">
                <CqButton
                    class="scrollButton scrollButtonLeft"
                    shape="round"
                    margin="none"
                    size="sm"
                    tooltip="Scroll naar eerder"
                    :icon="'arrow-left'"
                    :primary="true"
                    :disabled="!canScrollLeft"
                    @click="scrollLeft"
                />
                <CqButton
                    class="scrollButton scrollButtonRight"
                    shape="round"
                    margin="none"
                    size="sm"
                    tooltip="Scroll naar later"
                    :icon="'arrow-right'"
                    :primary="true"
                    :disabled="!canScrollRight"
                    @click="scrollRight"
                />
                <div class="workScheduleGridContainer" v-if="hasActiveDay">
                    <TimeBar :renderedDays="renderedDays" label="Medewerkers" :activeDay="activeDay!.date" />
                    <DeploymentHolders
                        :organisationDeploymentStates="organisation!.organisationDeploymentStates"
                        :groupLabels="workScheduleGroupLabels"
                        :enabledCompanies="enabledCompanies"
                        :workScheduleAssignmentsByDeployment="workScheduleAssignmentsInVisibleRangeByDeployment"
                        :workSchedulePositions="allWorkSchedulePositionsForRenderedDateRange"
                        :workingDeploymentStates="workingDeploymentStates"
                        :visibleDeploymentSpecificationTypeIds="visibleDeploymentSpecificationTypeIds"
                        :deploymentsAvailableTimes="timeBlocksWithoutWorkScheduleAssignmentPerDeployment"
                        :deploymentHolders="visibleWorkScheduleDeploymentHolders"
                        :roster="roster"
                        :rosterNames="rosterNames"
                        :shifts="shifts"
                        :renderedDateRange="renderedDateRange"
                        :deploymentsNotAvailableForDrop="deploymentsNotAvailableForDrop"
                        :specificationsNotAvailableForDrop="specificationsNotAvailableForDrop"
                        :workSchedulePreferences="workSchedulePreferences"
                        @dragStart:workScheduleAssignment="dragStartWorkScheduleAssignment"
                        @dragEnd:workScheduleAssignment="clearNotDroppableDeploymentsAndSpecifications"
                        @reload:workSchedulePosition="reloadPosition"
                    />
                </div>
            </div>
        </div>
        <TooltipWorkSchedule
            :workScheduleGroupLabels="workScheduleGroupLabels"
            @remove:workScheduleAssignment="removeWorkScheduleAssignment"
        />
    </CqModal>
</template>

<script lang="ts">
    import {Component, Vue, Prop, Watch, Ref} from 'vue-facing-decorator';
    import {RosterDay, RosterData, RenderedDay, NullableDateRange, DateRange, WorkScheduleDeploymentHolder, WorkSchedulePreference} from '@/types/types';
    import {
        ApiClient,
        DateString,
        Deployment,
        DeploymentSpecification,
        DeploymentStateSpecification,
        DeploymentType,
        DeploymentTypeProperties,
        Organisation,
        ResourceFunction,
        Shift,
        WorkScheduleAssignment,
        WorkScheduleGroupLabel,
        WorkSchedulePosition,
        WorkSchedulePositionFunctionRequirement,
        ApiError,
    } from '@cquential/api';
    import {PlanbordWorkScheduleGroupLabel, PlanbordWorkSchedulePosition, ResourceWithKeyedByDateObjects, CompanyWithDeploymentsAndEmployees, CompanyEmployeeWithDeployments, PlanbordResource} from '@/types/api-object-extensions';
    import {CqButton, CqModal, UserNotification} from '@cquential/ui';
    import WorkScheduleGroupLabels from '@/components/WorkSchedule/WorkScheduleGroupLabels.vue';
    import Notify from '@/components/modals/Notify.vue';
    import Dates, {type DateCompare} from '@cquential/date-utils';
    import TimeBar from '@/components/WorkSchedule/TimeBar.vue';
    import BoardDivider from '@/components/Planbord/BoardDivider.vue';
    import DisplayOptionsMenu from '@/components/WorkSchedule/DisplayOptionsMenu.vue';
    import TooltipWorkSchedule from '@/components/WorkSchedule/Tooltip/TooltipWorkSchedule.vue';
    import FullyAssigned from '@/components/WorkSchedule/FullyAssigned.vue';
    import DeploymentHolders from '@/components/WorkSchedule/DeploymentHolders.vue';
    import WorkScheduleUtil from '@/util/WorkScheduleUtil';
    import Loader from '@/components/modals/Loader.vue';
    import {CSSProperties} from 'vue';
    import GenericConfirmationModal from '@/components/modals/GenericConfirmationModal.vue';
    import {ButtonColorScheme} from '@/types/enums/ButtonColorScheme';
    import {ResourceFunctionIdsAndDeploymentIdsMap} from '@/types';
    import DeploymentUtil from '@/util/deploymentutil';
    import GlobalErrorModal from '@/components/modals/GlobalErrorModal.vue';
    import {ShiftTemplateType} from '@/types/enums/ShiftTemplateType';

    @Component({
        components: {
            CqButton,
            CqModal,
            WorkScheduleGroupLabels,
            TimeBar,
            BoardDivider,
            DisplayOptionsMenu,
            TooltipWorkSchedule,
            FullyAssigned,
            DeploymentHolders,
        },
        emits: [
            'update:workSchedules',
            'close:workSchedule',
        ],
    })
    export default class WorkSchedule extends Vue {
        private static instance: WorkSchedule | null = null;

        @Prop({required: true}) public fullRoster!: RosterData;
        @Prop({required: true}) public enabledCompanies!: CompanyWithDeploymentsAndEmployees[];
        @Prop({required: true}) public organisation!: Organisation | null;
        @Prop({required: true}) public shifts!: Record<number, Shift>;
        @Prop({required: true}) public rosterNames!: Record<number, string>;

        @Ref() private readonly content!: HTMLDivElement;
        @Ref() private readonly workSchedulePositionsHolder!: HTMLDivElement;
        @Ref() private readonly deploymentsHolder!: HTMLDivElement;

        private readonly DIVIDER_MIN_HEIGHT: number = 100;

        private renderedDateRange: NullableDateRange = {begin: null, end: null};
        private activeDay: RosterDay | null = null;
        private firstVisibleDate: Date | null = null;

        private show: boolean = false;
        private isAutomaticAssigning: boolean = false;
        private isLoadingWorkScheduleGroupLabels: boolean = false;
        private isRemovingAssignments: boolean = false;
        private hasFilterablePositionTypes: boolean = false;
        private deploymentsNotAvailableForDrop: number[] = [];
        private specificationsNotAvailableForDrop: number[] = [];

        private workScheduleGroupLabels: Record<number, PlanbordWorkScheduleGroupLabel> = {};
        private workSchedulePreferences: WorkSchedulePreference = {
            employeeSorting: 'rosterRank',
            showAllWorkSchedules: false,
            viewReachable: false,
        };

        private allWorkSchedulePositions: Record<'working' | 'reachable', Record<string, Record<number, PlanbordWorkSchedulePosition>>> = {reachable: {}, working: {}};
        private workSchedulesHeight: number = 0;
        private workingDeploymentStates: DeploymentStateSpecification[] = [];

        @Watch('maxDeploymentId')
        public onMaxDeploymentIdChange(): void {
            if (this.renderedDateRange.begin !== null && this.renderedDateRange.end !== null) {
                this.loadPositions(this.renderedDateRange.begin!, Dates.Utils.endOfDay(this.renderedDateRange.end!), false, true);
            }
        }

        @Watch('workSchedulePreferences.showAllWorkSchedules')
        public onShowAllWorkSchedulesChange(): void {
            this.checkDividerHeight();
        }

        public mounted(): void {
            WorkSchedule.instance = this;
        }

        public beforeUnmount(): void {
            WorkSchedule.instance = null;
        }

        public get hasActiveDay(): boolean {
            return this.roster?.days?.length > 0
                && this.activeDay !== null
                && this.renderedDateRange.begin !== null;
        }

        public get spaceHolderLeftHours(): number {
            return (this.renderedDateRange.begin!.getTime() - this.firstRosterDay.getTime()) / Dates.Time.MILLI_SECONDS_IN_ONE_HOUR;
        }

        public get cssCustomProperties(): CSSProperties {
            if (!this.hasActiveDay) {
                return {};
            }

            return {
                '--hour-cell-width': WorkScheduleUtil.HOURS_CELL_WIDTH_IN_REM + 'rem',
                '--label-cell-width': WorkScheduleUtil.LABEL_CELL_WIDTH_IN_REM + 'rem',
                '--roster-date-range-in-hours': Math.round((Dates.Utils.endOfDay(this.lastRosterDay).getTime() - this.firstRosterDay.getTime()) / Dates.Time.MILLI_SECONDS_IN_ONE_HOUR),
                '--left-space-holder-hours': this.spaceHolderLeftHours,
                '--left-place-holder-display': this.spaceHolderLeftHours === 0 ? 'none' : 'block',
                '--right-space-holder-hours': (Dates.Utils.endOfDay(this.lastRosterDay).getTime() - this.renderedDateRange.end!.getTime()) / Dates.Time.MILLI_SECONDS_IN_ONE_HOUR,
            };
        }

        public get workSchedulesHeightAsString(): string {
            if (this.workSchedulesHeight === 0) {
                return 'max-content';
            }

            return this.workSchedulesHeight + 'px';
        }

        private get visibleRangeInHours(): number {
            const labelColumnWidth: number = WorkScheduleUtil.LABEL_CELL_WIDTH_IN_REM * WorkScheduleUtil.REM_IN_PX;

            return (this.deploymentsHolder.getBoundingClientRect().width - labelColumnWidth) / WorkScheduleUtil.HOURS_CELL_WIDTH_IN_PX;
        }

        private get firstRosterDay(): Date {
            return this.roster.days.at(0)?.date ?? this.roster.beginDate ?? Dates.Utils.startOfYear();
        }

        private get lastRosterDay(): Date {
            return this.roster.days.at(-1)?.date ?? this.roster.endDate ?? Dates.Utils.endOfYear();
        }

        private get lastVisibleDate(): Date | null {
            if (!this.firstVisibleDate) {
                return null;
            }

            // if the visible range is 10.9 hours, you must render up to 12 hours at worst: partially visible first + 10 fully visible + partially visible last
            // if it is 11, this is the same
            // if it is 11.01, it may be 13 hours, e.g. 1px of the first, 11 fully visible, 1px of the last
            return Dates.Utils.addHours(this.firstVisibleDate, Math.ceil(this.visibleRangeInHours + 1));
        }

        public get heading(): string {
            if (!this.hasActiveDay) {
                return 'Werkindelingen';
            }

            return 'Werkindelingen voor ' + this.activeDay!.formattedDateOnly;
        }

        public get automaticAssignLabel(): string {
            return 'Automatisch indelen op ' + Dates.Formatter.formatDate(this.activeDay!.date);
        }

        public get removeAssignedLabel(): string {
            return 'Alle toegewezen werkindelingen verwijderen op ' + Dates.Formatter.formatDate(this.activeDay!.date);
        }

        private get renderedDateRangeAsPositionKeys(): string[] {
            return this.formatDateRangeToPositionDateKeys({begin: this.renderedDateRange.begin!, end: this.renderedDateRange.end!});
        }

        public get renderedDateRangeAsRosterDays(): RosterDay[] {
            return WorkScheduleUtil.convertDateRangeToRosterDays(this.renderedDateRange, this.roster);
        }

        public get renderedDays(): Record<string, RenderedDay> {
            const renderedDays: Record<string, RenderedDay> = {};

            for (const day of this.renderedDateRangeAsRosterDays) {
                const date: Date = Dates.Utils.dateStringToAmsterdamMidnightDate(day.formattedForComputers);

                const hoursOfDay: string[] = [Dates.Formatter.formatTime(date)];
                while (true) {
                    date.setTime(date.getTime() + (1000 * 3600));
                    const formatted: string = Dates.Formatter.formatTime(date);
                    hoursOfDay.push(formatted);
                    if (formatted === '00:00' || hoursOfDay.length === 26) {
                        // length check to prevent infinite loops if browsers have unexpected formatting (highly unlikely)
                        break;
                    }
                }

                if (!renderedDays[day.formattedForComputers]) {
                    renderedDays[day.formattedForComputers] = {
                        date: new Date(day.date),
                        formattedDateIso: day.formattedDateIso,
                        label: Dates.Formatter.formatDateWithWeekDayLongMonth(day.date),
                        columns: {},
                    };
                }

                const n: number = hoursOfDay.length - 1;
                for (let i: number = 0; i < n; i++) {
                    renderedDays[day.formattedForComputers].columns[i] = {
                        begin: hoursOfDay[i],
                        end: hoursOfDay[i + 1],
                    };
                }
            }

            return renderedDays;
        }

        public get allWorkSchedulePositionsForRenderedDateRange(): Record<number, PlanbordWorkSchedulePosition> {
            const positions: Record<number, PlanbordWorkSchedulePosition> = {};
            for (const date of this.renderedDateRangeAsPositionKeys) {
                if (date in this.allWorkSchedulePositions.working) {
                    Object.assign(positions, this.allWorkSchedulePositions.working[date]);
                }
                if (date in this.allWorkSchedulePositions.reachable) {
                    Object.assign(positions, this.allWorkSchedulePositions.reachable[date]);
                }
            }

            return positions;
        }

        public get workSchedulePositionsForRenderedDateRange(): Record<number, PlanbordWorkSchedulePosition> {
            const positions: Record<number, PlanbordWorkSchedulePosition> = {};
            for (const date of this.renderedDateRangeAsPositionKeys) {
                if (date in this.workSchedulePositions) {
                    Object.assign(positions, this.workSchedulePositions[date]);
                }
            }

            return positions;
        }

        public get groupLabelsWithPositions(): Record<number, PlanbordWorkScheduleGroupLabel> {
            if (this.isLoadingWorkScheduleGroupLabels) {
                return {};
            }

            const groupLabels: Record<number, PlanbordWorkScheduleGroupLabel> = {};
            for (const position of Object.values(this.workSchedulePositionsForRenderedDateRange)) {
                if (!groupLabels[position.workScheduleGroupLabelId!]) {
                    groupLabels[position.workScheduleGroupLabelId!] = {...this.workScheduleGroupLabels[position.workScheduleGroupLabelId!], workSchedulePositions: {}};
                }

                groupLabels[position.workScheduleGroupLabelId!].workSchedulePositions![position.id!] = position;
            }

            return groupLabels;
        }

        public get filteredGroupLabelsWithPositions(): Record<number, PlanbordWorkScheduleGroupLabel> {
            if (this.workSchedulePreferences.showAllWorkSchedules) {
                return this.groupLabelsWithPositions;
            }

            const result: Record<number, PlanbordWorkScheduleGroupLabel> = {};
            for (const [id, groupLabel] of Object.entries(this.groupLabelsWithPositions)) {
                const workSchedulePositionsEntries: [string, PlanbordWorkSchedulePosition][] = Object.entries(groupLabel.workSchedulePositions!)
                    .filter(([, workSchedulePosition]: [string, PlanbordWorkSchedulePosition]): boolean => {
                        return workSchedulePosition.timeBlocksWithoutWorkScheduleAssignment.length > 0;
                    });

                if (workSchedulePositionsEntries.length > 0) {
                    result[Number(id)] = {...groupLabel, workSchedulePositions: Object.fromEntries(workSchedulePositionsEntries)};
                }
            }

            return result;
        }

        public get groupLabelsWithPositionsInVisibleDateRange(): Record<number, PlanbordWorkScheduleGroupLabel> {
            const groupLabelsWithPositionsInVisibleDateRange: Record<number, PlanbordWorkScheduleGroupLabel> = {};

            for (const groupsLabelIndex in this.filteredGroupLabelsWithPositions) {
                if (Object.values(this.filteredGroupLabelsWithPositions[groupsLabelIndex].workSchedulePositions ?? {}).some((workSchedulePosition: PlanbordWorkSchedulePosition): boolean => {
                    return WorkScheduleUtil.isDateRangeOverlapping({begin: workSchedulePosition.beginDateTime, end: workSchedulePosition.endDateTime}, this.renderedDateRange.begin!, this.renderedDateRange.end!);
                })) {
                    groupLabelsWithPositionsInVisibleDateRange[groupsLabelIndex] = {...this.filteredGroupLabelsWithPositions[groupsLabelIndex]};
                }
            }

            return groupLabelsWithPositionsInVisibleDateRange;
        }

        public get workingDeploymentStateIds(): number[] {
            return this.workingDeploymentStates.map((state: DeploymentStateSpecification): number => state.deploymentStateId);
        }

        public get workScheduleDeploymentHolders(): WorkScheduleDeploymentHolder[] {
            const employees: WorkScheduleDeploymentHolder[] = [
                ...this.resourcesWithDeployments,
                ...this.companiesWithDeploymentsAndEmployees,
                ...this.companiesWithDeploymentsAndEmployees.map((company: CompanyWithDeploymentsAndEmployees): CompanyEmployeeWithDeployments[] => Object.values(company.employees)).flat(),
            ]
                .map(this.mapToDeploymentHolderWithFilteredDeployments)
                .filter((deploymentHolder: WorkScheduleDeploymentHolder | null): deploymentHolder is WorkScheduleDeploymentHolder => deploymentHolder !== null);

            return WorkScheduleUtil.sortEmployees(employees, this.activeDay!.formattedDateIso, this.workSchedulePreferences.employeeSorting);
        }

        public get visibleWorkScheduleDeploymentHolders(): WorkScheduleDeploymentHolder[] {
            if (!this.firstVisibleDate || !this.lastVisibleDate) {
                return [];
            }

            return this.workScheduleDeploymentHolders.filter((deploymentHolder: WorkScheduleDeploymentHolder): boolean => {
                const deployments: Record<string, Deployment[]> = WorkScheduleUtil.getDeploymentHolderDeployments(deploymentHolder);
                for (const isoDateString in deployments) {
                    const deploymentsOnDay: Deployment[] = deployments[isoDateString] ?? [];
                    for (const deployment of deploymentsOnDay) {
                        if (!deployment.specifications || deployment.specifications.length === 0) {
                            continue;
                        }
                        if (WorkScheduleUtil.isDateRangeOverlapping({begin: deployment.specifications!.at(0)!.begin, end: deployment.specifications!.at(-1)!.end}, this.firstVisibleDate!, this.lastVisibleDate!)) {
                            return true;
                        }
                    }
                }

                return false;
            });
        }

        public get roster(): RosterData {
            return {
                ...this.fullRoster,
                rosterResources: this.fullRoster.rosterResources.map((resource: PlanbordResource): PlanbordResource => ({
                    ...resource,
                    deployments: this.getFilteredDeploymentsForActiveTypes(resource.deployments),
                })),
                resourcesFromOtherRosters: this.fullRoster.resourcesFromOtherRosters.map((resource: ResourceWithKeyedByDateObjects): ResourceWithKeyedByDateObjects => ({
                    ...resource,
                    deployments: this.getFilteredDeploymentsForActiveTypes(resource.deployments),
                })),
                relatedResources: this.fullRoster.relatedResources.map((resource: ResourceWithKeyedByDateObjects): ResourceWithKeyedByDateObjects => ({
                    ...resource,
                    deployments: this.getFilteredDeploymentsForActiveTypes(resource.deployments),
                })),
                externalResources: this.fullRoster.externalResources.map((resource: ResourceWithKeyedByDateObjects): ResourceWithKeyedByDateObjects => ({
                    ...resource,
                    deployments: this.getFilteredDeploymentsForActiveTypes(resource.deployments),
                })),

            };
        }

        public get workSchedulePositions(): Record<string, Record<number, PlanbordWorkSchedulePosition>> {
            if (this.workSchedulePreferences.viewReachable) {
                return this.allWorkSchedulePositions.reachable;
            } else {
                return this.allWorkSchedulePositions.working;
            }
        }

        public get allWorkSchedulePositionsByDateAsFlatArray(): Record<DateString, PlanbordWorkSchedulePosition[]> {
            const allWorkSchedulePositions: Record<DateString, PlanbordWorkSchedulePosition[]> = {};
            for (const date in this.allWorkSchedulePositions.reachable) {
                allWorkSchedulePositions[date] ??= [];
                allWorkSchedulePositions[date] = allWorkSchedulePositions[date].concat(Object.values(this.allWorkSchedulePositions.reachable[date]));
            }
            for (const date in this.allWorkSchedulePositions.working) {
                allWorkSchedulePositions[date] ??= [];
                allWorkSchedulePositions[date] = allWorkSchedulePositions[date].concat(Object.values(this.allWorkSchedulePositions.working[date]));
            }

            return allWorkSchedulePositions;
        }

        public getFilteredDeploymentsForActiveTypes(deployments: Record<string, Deployment[]>): Record<string, Deployment[]> {
            const result: Record<string, Deployment[]> = {};
            for (const dateString in deployments) {
                const deploymentsForDay: Deployment[] = this.getFilteredDeploymentForActiveType(deployments[dateString]);
                if (deploymentsForDay.length > 0) {
                    result[dateString] = deploymentsForDay;
                }
            }

            return result;
        }

        public getFilteredDeploymentForActiveType(deployments: Deployment[]): Deployment[] {
            return deployments
                .filter((deployment: Deployment): boolean => deployment.specifications!.some((specification: DeploymentSpecification): boolean => this.visibleDeploymentSpecificationTypeIds.includes(specification.typeId)))
                .map((deployment: Deployment): Deployment => ({
                    ...deployment,
                    specifications: deployment.specifications!.map((specification: DeploymentSpecification): DeploymentSpecification => ({...specification})),
                }));
        }

        public get visibleDeploymentSpecificationTypeIds(): number[] {
            return this.deploymentSpecificationTypes(this.workSchedulePreferences.viewReachable ? 'reachable' : 'workingOrPresent');
        }

        private deploymentSpecificationTypes(propertyToFilterFor: keyof DeploymentTypeProperties): number[] {
            if (!this.organisation) {
                return [];
            }

            const deploymentSpecificationTypes: number[] = [];
            for (const typeId in this.organisation.organisationDeploymentTypes) {
                const type: DeploymentType = this.organisation.organisationDeploymentTypes[typeId];
                if (type.properties[propertyToFilterFor]) {
                    deploymentSpecificationTypes.push(Number(typeId));
                }
            }

            return deploymentSpecificationTypes;
        }

        public get resourcesWithDeployments(): (PlanbordResource | ResourceWithKeyedByDateObjects)[] {
            return [...this.roster.rosterResources, ...this.roster.resourcesFromOtherRosters].filter((resource: PlanbordResource | ResourceWithKeyedByDateObjects): boolean => {
                for (const day of this.renderedDateRangeAsRosterDays) {
                    if (resource.deployments[day.formattedDateIso]?.some((deployment: Deployment): boolean => this.shifts[deployment.shiftId].rosterId === this.roster.rosterId && this.hasSpecification(deployment))) {
                        return true;
                    }
                }

                return false;
            });
        }

        public get companiesWithDeploymentsAndEmployees(): CompanyWithDeploymentsAndEmployees[] {
            if (!this.activeDay) {
                return [];
            }

            return this.enabledCompanies.filter((company: CompanyWithDeploymentsAndEmployees): boolean => {
                for (const day of this.renderedDateRangeAsRosterDays) {
                    if (
                        company.deploymentsWithoutEmployee[day.formattedDateIso]?.some(this.hasSpecification)
                        || Object.values(company.employees).some((employee: CompanyEmployeeWithDeployments): boolean => employee.deployments[day.formattedDateIso]?.some(this.hasSpecification))
                    ) {
                        return true;
                    }
                }

                return false;
            });
        }

        public get maxDeploymentId(): number {
            return [...this.roster.rosterResources, ...this.roster.resourcesFromOtherRosters].reduce<number>((previous: number, resource: ResourceWithKeyedByDateObjects): number => {
                return Math.max(
                    previous,
                    ...Object.values(resource.deployments).flat().map((deployment: Deployment): number => deployment.id ?? Number.MIN_SAFE_INTEGER),
                );
            }, Number.MIN_SAFE_INTEGER);
        }

        public get allDeploymentsWithSpecificationsById(): Record<number, Deployment> {
            const result: Record<number, Deployment> = {};
            const deployments: Deployment[] = [
                ...this.resourcesWithDeployments.map((resource: ResourceWithKeyedByDateObjects): Deployment[] => {
                    const resourceDeployments: Deployment[] = [];
                    for (const day of this.renderedDateRangeAsRosterDays) {
                        resourceDeployments.push(...resource.deployments[day.formattedDateIso] ?? []);
                    }

                    return resourceDeployments;
                }),
                ...this.companiesWithDeploymentsAndEmployees.map((company: CompanyWithDeploymentsAndEmployees): Deployment[] => {
                    const companyDeployments: Deployment[] = [];
                    for (const day of this.renderedDateRangeAsRosterDays) {
                        companyDeployments.push(...company.deploymentsWithoutEmployee[day.formattedDateIso] ?? []);
                        companyDeployments.push(...Object.values(company.employees).map((companyEmployee: CompanyEmployeeWithDeployments): Deployment[] => {
                            return companyEmployee.deployments[day.formattedDateIso] ?? [];
                        }).flat());
                    }

                    return companyDeployments;
                }),
            ].flat().filter(this.hasSpecification);

            for (const deployment of deployments) {
                if (deployment.id) {
                    result[deployment.id] = deployment;
                }
            }

            return result;
        }

        public get timeBlocksWithoutWorkScheduleAssignmentPerDeployment(): Record<number, DateRange[]> {
            const result: Record<number, DateRange[]> = {};

            for (const deploymentId in this.allDeploymentsWithSpecificationsById) {
                const deployment: Deployment = this.allDeploymentsWithSpecificationsById[deploymentId];
                const unavailableDateRanges: DateRange[] = [];
                for (const specification of deployment.specifications ?? []) {
                    if (!this.isSpecificationAssignable(specification, this.visibleDeploymentSpecificationTypeIds)) {
                        unavailableDateRanges.push({begin: specification.begin, end: specification.end});
                    }
                }

                result[deployment.id!] = WorkScheduleUtil.timeBlocksWithoutWorkScheduleAssignments(
                    this.workScheduleAssignmentsInVisibleRangeByDeployment[deployment.id!] ?? [], deployment.specifications![0]!.begin, deployment.specifications![deployment.specifications!.length - 1]!.end, unavailableDateRanges,
                );
            }

            return result;
        }

        public get resourceFunctionIdsAndDeploymentIdsMap(): ResourceFunctionIdsAndDeploymentIdsMap {
            const result: ResourceFunctionIdsAndDeploymentIdsMap = {};

            for (const resourceWithDeployments of this.resourcesWithDeployments) {
                result[resourceWithDeployments.id!] = {
                    deploymentIds: Object.values(resourceWithDeployments.deployments).flat().map((deployment: Deployment): number => deployment.id!),
                    functionIds: resourceWithDeployments.functions?.map((resourceFunction: ResourceFunction): number => resourceFunction.id!) ?? [],
                };
            }

            return result;
        }

        public get workScheduleAssignmentIdsOnActiveDay(): number[] {
            const compareActiveDate: DateCompare = Dates.compare(this.activeDay!.date);
            const result: number[] = [];

            for (const assignment of this.allWorkScheduleAssignments) {
                if (compareActiveDate.isSameDay(assignment.beginDateTime)) {
                    result.push(assignment.id!);
                }
            }

            return result;
        }

        public get hasWorkScheduleAssignmentsForActiveDay(): boolean {
            return this.workScheduleAssignmentIdsOnActiveDay.length > 0;
        }

        public resetWorkScheduleGroupLabelLoadedPositions(): void {
            for (const groupLabel of Object.values(this.workScheduleGroupLabels)) {
                groupLabel.workSchedulePositions = {};
            }
        }

        public resetWorkSchedulePreferencesTypeFilter(): void {
            if (!this.hasFilterablePositionTypes && this.workSchedulePreferences.viewReachable) {
                this.workSchedulePreferences.viewReachable = false;
            }
        }

        public updateWorkSchedulePreferencesBasedOnLoadedPositions(): void {
            const hasReachablePositions: boolean = Object.values(this.allWorkSchedulePositionsByDateAsFlatArray).some((positionsPerDate: PlanbordWorkSchedulePosition[]): boolean => positionsPerDate.some((position: PlanbordWorkSchedulePosition): boolean => position.isReachable));
            const hasWorkablePositions: boolean = Object.values(this.allWorkSchedulePositionsByDateAsFlatArray).some((positionsPerDate: PlanbordWorkSchedulePosition[]): boolean => positionsPerDate.some((position: PlanbordWorkSchedulePosition): boolean => !position.isReachable));

            this.hasFilterablePositionTypes = hasReachablePositions && hasWorkablePositions;

            if (!hasReachablePositions && hasWorkablePositions && this.workSchedulePreferences.viewReachable) {
                this.workSchedulePreferences.viewReachable = false;
            } else if (hasReachablePositions && !hasWorkablePositions && !this.workSchedulePreferences.viewReachable) {
                this.workSchedulePreferences.viewReachable = true;
            }
        }

        public removeWorkScheduleAssignment(workScheduleAssignment: WorkScheduleAssignment): void {
            /** @see GenericConfirmationModal.openDefaultConfirmationModel() */
            // @ts-expect-error TS2339
            GenericConfirmationModal.__vfdConstructor.openDefaultConfirmationModel('Werkindelingtoewijzing verwijderen', 'Weet u zeker dat u deze werkindelingtoewijzing wilt verwijderen?')
                .then((result: 'CANCEL' | 'OK'): void => {
                    if (result === 'CANCEL') {
                        return;
                    }
                    this.setIsLoading();

                    const workScheduleVisibleType: keyof typeof this.allWorkSchedulePositions = this.workSchedulePreferences.viewReachable ? 'reachable' : 'working';

                    ApiClient.instance.deleteWorkScheduleAssignment(workScheduleAssignment.id!)
                        .then((): void => {
                            this.createNotification({text: 'Werkindelingtoewijzing verwijderd', type: 'success'});
                            this.setIsLoaded();

                            const dayKey: string = this.formatPositionDateKey(workScheduleAssignment.beginDateTime);
                            const workSchedulePosition: PlanbordWorkSchedulePosition = this.allWorkSchedulePositions[workScheduleVisibleType][dayKey][workScheduleAssignment.workSchedulePositionId!];
                            const assignmentIndex: number = workSchedulePosition.workScheduleAssignments!.findIndex((assignment: WorkScheduleAssignment): boolean => assignment.id === workScheduleAssignment.id);

                            workSchedulePosition.workScheduleAssignments!.splice(assignmentIndex, 1);
                            workSchedulePosition.timeBlocksWithoutWorkScheduleAssignment = WorkScheduleUtil.timeBlocksWithoutWorkScheduleAssignments(workSchedulePosition.workScheduleAssignments!, workSchedulePosition.beginDateTime, workSchedulePosition.endDateTime);
                        })
                        .catch((error: ApiError): void => {
                            this.createNotification({text: 'Werkindelingtoewijzing kon niet verwijderd worden', type: 'error'});
                            console.error(error);
                            this.setLoadingError();
                        });
                });
        }

        private setIsLoading(): void {
            /** @see Loader.setLoading() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setLoading('workSchedule');
        }

        private setIsLoaded(): void {
            /** @see Loader.setLoaded() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setLoaded('workSchedule');
        }

        private setLoadingError(): void {
            /** @see Loader.setError() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setError('workSchedule');
        }

        private createNotification(message: UserNotification): void {
            /** @see Notify.createNotification() */
            // @ts-expect-error TS2339
            Notify.__vfdConstructor.createNotification(message);
        }

        private setScrollPosition(dateTimeToScrollTo: Date): void {
            const deltaInHours: number = (dateTimeToScrollTo.getTime() - this.renderedDateRange.begin!.getTime()) / Dates.Time.MILLI_SECONDS_IN_ONE_HOUR;
            this.deploymentsHolder.scrollLeft = (this.spaceHolderLeftHours * WorkScheduleUtil.HOURS_CELL_WIDTH_IN_PX) + (deltaInHours * WorkScheduleUtil.HOURS_CELL_WIDTH_IN_PX);
            this.syncScrollPositions();
            this.updateFirstVisibleDate();
        }

        private get scrollButtonStep(): number {
            return this.visibleRangeInHours / 3;
        }

        public get canScrollLeft(): boolean {
            return this.firstVisibleDate !== null
                && this.firstVisibleDate > this.firstRosterDay;
        }

        public scrollLeft(): void {
            if (this.canScrollLeft) {
                this.firstVisibleDate = Dates.Utils.addHours(this.firstVisibleDate!, -1 * this.scrollButtonStep);
                if (this.firstVisibleDate < this.firstRosterDay) {
                    this.firstVisibleDate = this.firstRosterDay;
                }
                this.setScrollPosition(this.firstVisibleDate);

                this.updateActiveDay();
                this.loadPreviousOrNextDay();
            }
        }

        public get canScrollRight(): boolean {
            return this.firstVisibleDate !== null
                && this.firstVisibleDate < Dates.Utils.addHours(this.lastRosterDay, 24 - this.visibleRangeInHours);
        }

        public scrollRight(): void {
            if (this.canScrollRight) {
                const maxScrollDate: Date = Dates.Utils.addHours(Dates.Utils.endOfDay(this.lastRosterDay), -1 * this.visibleRangeInHours);
                this.firstVisibleDate = Dates.Utils.addHours(this.firstVisibleDate!, this.scrollButtonStep);
                if (this.firstVisibleDate > maxScrollDate) {
                    this.firstVisibleDate = maxScrollDate;
                }
                this.setScrollPosition(this.firstVisibleDate);
                this.updateActiveDay();
                this.loadPreviousOrNextDay();
            }
        }

        public hasSpecification(deployment: Deployment): boolean {
            return (deployment.specifications && deployment.specifications.length > 0) || false;
        }

        public isSpecificationAssignable(specification: DeploymentSpecification, validSpecificationTypeIds: number[]): boolean {
            return this.workingDeploymentStateIds.includes(specification.stateId)
                && validSpecificationTypeIds.includes(specification.typeId)
                && !!this.organisation?.organisationDeploymentStates[specification.stateId]?.properties.fulfillsRequiredShift;
        }

        public filterVisibleDeployments(deployments: Record<string, Deployment[]>): Record<string, Deployment[]> {
            const timeBlocks: Record<number, DateRange[]> = this.timeBlocksWithoutWorkScheduleAssignmentsByDeployment(deployments);

            const filteredDeployments: Record<string, Deployment[]> = {...deployments};
            for (const day of this.renderedDateRangeAsRosterDays) {
                filteredDeployments[day.formattedDateIso] = (deployments[day.formattedDateIso] ?? []).filter((deployment: Deployment): boolean => {
                    if (!this.workSchedulePreferences.showAllWorkSchedules) {
                        if (this.deploymentsNotAvailableForDrop.includes(deployment.id!)) {
                            return false;
                        } else if (timeBlocks[deployment.id!] && timeBlocks[deployment.id!].length === 0) {
                            return false;
                        }
                    }

                    return this.workingDeploymentStateIds.includes(deployment.stateId);
                });
                DeploymentUtil.sortOnSpecificationBeginTimesAscending(filteredDeployments[day.formattedDateIso]);
            }

            return filteredDeployments;
        }

        public mapToDeploymentHolderWithFilteredDeployments<T extends WorkScheduleDeploymentHolder>(deploymentHolder: T): WorkScheduleDeploymentHolder | null {
            if (this.workSchedulePreferences.showAllWorkSchedules) {
                return deploymentHolder;
            }

            if (WorkScheduleUtil.isDeploymentHolderACompany(deploymentHolder)) {
                const filteredCompany: typeof deploymentHolder = {
                    ...deploymentHolder,
                    deploymentsWithoutEmployee: this.filterVisibleDeployments(deploymentHolder.deploymentsWithoutEmployee),
                };

                for (const day of this.renderedDateRangeAsRosterDays) {
                    if (filteredCompany.deploymentsWithoutEmployee[day.formattedDateIso].length > 0) {
                        return filteredCompany;
                    }
                }
            } else if (WorkScheduleUtil.isDeploymentHolderWithDeploymentProperty(deploymentHolder)) {
                const filteredData: typeof deploymentHolder = {
                    ...deploymentHolder,
                    deployments: this.filterVisibleDeployments(deploymentHolder.deployments),
                };

                for (const day of this.renderedDateRangeAsRosterDays) {
                    if (filteredData.deployments[day.formattedDateIso]?.length > 0) {
                        return filteredData;
                    }
                }
            }

            return null;
        }

        private loadWorkingDeploymentStates(): Promise<void> {
            if (this.workingDeploymentStates.length > 0) {
                return Promise.resolve();
            }

            return ApiClient.instance.fetchDeploymentStatesByType('working')
                .then((deploymentStates: DeploymentStateSpecification[]): void => {
                    this.workingDeploymentStates = deploymentStates;
                });
        }

        private loadLabels(): Promise<void> {
            if (this.isLoadingWorkScheduleGroupLabels || Object.keys(this.workScheduleGroupLabels).length > 0) {
                return Promise.resolve();
            }

            this.isLoadingWorkScheduleGroupLabels = true;
            this.workScheduleGroupLabels = {};

            return ApiClient.instance.fetchWorkScheduleGroupLabels()
                .then((loadedGroupLabels: WorkScheduleGroupLabel[]): void => {
                    for (const groupLabel of loadedGroupLabels) {
                        this.workScheduleGroupLabels[groupLabel.id!] = WorkScheduleUtil.convertWorkScheduleGroupLabelToPlanbordWorkScheduleGroupLabel(groupLabel);
                    }
                })
                /** @see GlobalErrorModal.globalErrorHandler() */
                // @ts-expect-error TS2339
                .catch(GlobalErrorModal.__vfdConstructor.globalErrorHandler)
                .finally((): void => {
                    this.isLoadingWorkScheduleGroupLabels = false;
                });
        }

        private loadPositionsForDay(day: Date, forceLoad: boolean = false): Promise<void> {
            const dateKey: string = this.formatPositionDateKey(day);
            if (dateKey in this.workSchedulePositions && !forceLoad) {
                return Promise.resolve();
            }

            return this.loadPositions(Dates.Utils.startOfDay(day), Dates.Utils.endOfDay(day), false, false);
        }

        private reloadPosition(workSchedulePositionId: number): Promise<void> {
            this.setIsLoading();

            return ApiClient.instance.fetchWorkSchedulePosition(workSchedulePositionId, this.roster.rosterId, ['workScheduleAssignments', 'functionRequirements'])
                .then((loadedWorkSchedulePosition: WorkSchedulePosition): void => {
                    this.addWorkSchedulePosition(loadedWorkSchedulePosition);
                })
                .finally((): void => {
                    this.setIsLoaded();
                });
        }

        private loadPositions(start: Date, end: Date, notifyNoPositions: boolean = true, clearAllPositions: boolean = true): Promise<void> {
            this.setIsLoading();

            return ApiClient.instance.fetchWorkSchedulePositions(start, end, this.roster.rosterId, ['workScheduleAssignments', 'functionRequirements'])
                .then((loadedWorkSchedulePositions: WorkSchedulePosition[]): void => {
                    if (loadedWorkSchedulePositions.length === 0 && notifyNoPositions) {
                        let dateRange: string = Dates.Formatter.formatDateWithWeekDayLongMonth(start);
                        if (!Dates.compare(start).isSameDay(end)) {
                            dateRange += ' - ' + Dates.Formatter.formatDateWithWeekDayLongMonth(end);
                        }
                        this.createNotification({text: `Er zijn nog geen werkindelingen (uitgerold) op ${dateRange}.`, type: 'warning'});

                        return;
                    }

                    if (clearAllPositions) {
                        this.allWorkSchedulePositions = {reachable: {}, working: {}};
                    }

                    for (const workSchedulePosition of loadedWorkSchedulePositions) {
                        this.addWorkSchedulePosition(workSchedulePosition);
                    }

                    for (const key of this.formatDateRangeToPositionDateKeys({begin: start, end: end})) {
                        this.allWorkSchedulePositions.reachable[key] ??= {};
                        this.allWorkSchedulePositions.working[key] ??= {};
                    }
                    this.updateWorkSchedulePreferencesBasedOnLoadedPositions();
                })
                /** @see GlobalErrorModal.globalErrorHandler() */
                // @ts-expect-error TS2339
                .catch(GlobalErrorModal.__vfdConstructor.globalErrorHandler)
                .finally((): void => {
                    this.setIsLoaded();
                });
        }

        private addWorkSchedulePosition(workSchedulePosition: WorkSchedulePosition): void {
            const planbordWorkSchedulePosition: PlanbordWorkSchedulePosition = WorkScheduleUtil.convertWorkSchedulePositionToPlanbordWorkSchedulePosition(workSchedulePosition);

            const positionType: keyof typeof this.allWorkSchedulePositions = workSchedulePosition.isReachable ? 'reachable' : 'working';
            const beginDate: string = this.formatPositionDateKey(workSchedulePosition.beginDateTime);
            this.allWorkSchedulePositions[positionType][beginDate] ??= {};
            this.allWorkSchedulePositions[positionType][beginDate][workSchedulePosition.id!] = planbordWorkSchedulePosition;

            const endDate: string = this.formatPositionDateKey(workSchedulePosition.endDateTime);
            if (endDate !== beginDate) {
                this.allWorkSchedulePositions[positionType][endDate][workSchedulePosition.id!] = planbordWorkSchedulePosition;
            }
        }

        private formatDateRangeToPositionDateKeys(range: DateRange): string[] {
            const keys: string[] = [];
            let date: Date = range.begin;
            while (Dates.compare(date).isBeforeOrEqual(range.end)) {
                keys.push(this.formatPositionDateKey(date));
                date = Dates.Utils.addDays(date, 1);
            }

            return keys;
        }

        private formatPositionDateKey(date: Date): string {
            return Dates.Formatter.formatDateComputerFriendly(date);
        }

        public get allWorkScheduleAssignments(): WorkScheduleAssignment[] {
            const result: WorkScheduleAssignment[] = [];
            for (const workScheduleGroupLabel of Object.values(this.groupLabelsWithPositions)) {
                for (const workSchedulePosition of Object.values(workScheduleGroupLabel.workSchedulePositions!)) {
                    result.push(...Object.values(workSchedulePosition.workScheduleAssignments!));
                }
            }

            return result;
        }

        public get workScheduleAssignmentsInVisibleRange(): WorkScheduleAssignment[] {
            return this.allWorkScheduleAssignments.filter((assignment: WorkScheduleAssignment): boolean => WorkScheduleUtil.isDateRangeOverlapping({begin: assignment.beginDateTime, end: assignment.endDateTime}, this.firstVisibleDate!, this.lastVisibleDate!));
        }

        public get workScheduleAssignmentsInVisibleRangeByDeployment(): Record<number, WorkScheduleAssignment[]> {
            const assignmentsPerDeploymentId: Record<number, WorkScheduleAssignment[]> = {};
            for (const workScheduleAssignment of this.workScheduleAssignmentsInVisibleRange) {
                assignmentsPerDeploymentId[workScheduleAssignment.deploymentId] ??= [];
                assignmentsPerDeploymentId[workScheduleAssignment.deploymentId].push(workScheduleAssignment);
            }

            for (const workScheduleAssignment of Object.values(assignmentsPerDeploymentId)) {
                workScheduleAssignment.sort((a: WorkScheduleAssignment, b: WorkScheduleAssignment): number => {
                    return a.beginDateTime.getTime() - b.beginDateTime.getTime();
                });
            }

            return assignmentsPerDeploymentId;
        }

        public timeBlocksWithoutWorkScheduleAssignmentsByDeployment(deployments: Record<string, Deployment[]>): Record<number, DateRange[]> {
            const result: Record<number, DateRange[]> = {};
            for (const day of this.renderedDateRangeAsRosterDays) {
                for (const deployment of deployments[day.formattedDateIso] ?? []) {
                    if (this.timeBlocksWithoutWorkScheduleAssignmentPerDeployment[deployment.id!]) {
                        result[deployment.id!] = this.timeBlocksWithoutWorkScheduleAssignmentPerDeployment[deployment.id!];
                    }
                }
            }

            return result;
        }

        public dragStartWorkSchedulePosition(workSchedule: PlanbordWorkSchedulePosition): void {
            let beginDateTime: Date | null = null;
            let endDateTime: Date | null = null;
            for (const dateRange of Object.values(workSchedule.timeBlocksWithoutWorkScheduleAssignment)) {
                if (!beginDateTime || dateRange.begin < beginDateTime) {
                    beginDateTime = dateRange.begin;
                }
                if (!endDateTime || dateRange.end > endDateTime) {
                    endDateTime = dateRange.end;
                }
            }

            if (!beginDateTime || !endDateTime) {
                return;
            }

            this.setNotAvailableSpecificationsAndDeployments(beginDateTime, endDateTime, workSchedule);
        }

        public dragStartWorkScheduleAssignment(workScheduleAssignment: WorkScheduleAssignment): void {
            const key: string = this.formatPositionDateKey(workScheduleAssignment.beginDateTime);
            this.setNotAvailableSpecificationsAndDeployments(
                workScheduleAssignment.beginDateTime,
                workScheduleAssignment.endDateTime,
                this.workSchedulePositions[key][workScheduleAssignment.workSchedulePositionId],
                [workScheduleAssignment.deploymentId],
            );
        }

        public setNotAvailableSpecificationsAndDeployments(
            beginDateTime: Date,
            endDateTime: Date,
            workSchedulePosition: PlanbordWorkSchedulePosition,
            additionalDeploymentIds: number[] = [],
        ): void {
            const invalidDeploymentIds: number[] = [...additionalDeploymentIds];
            const invalidSpecificationIds: number[] = [];
            const validTypeIds: number[] = this.deploymentSpecificationTypes(workSchedulePosition.isReachable ? 'reachable' : 'workingOrPresent');
            const validShiftTemplateTypes: string[] = [ShiftTemplateType.FULFILLS_REQUIRED_SHIFT, ShiftTemplateType.RESERVE, ShiftTemplateType.REACHABLE];

            for (const deploymentId in this.allDeploymentsWithSpecificationsById) {
                const deployment: Deployment = this.allDeploymentsWithSpecificationsById[deploymentId];
                if (!validShiftTemplateTypes.includes(this.shifts[deployment.shiftId].template!.type) || !this.workingDeploymentStateIds.includes(deployment.stateId)) {
                    invalidDeploymentIds.push(Number(deploymentId));
                    continue;
                }

                const invalidSpecificationIdsForThisDeployment: number[] = [];
                for (const specification of deployment.specifications!) {
                    if (
                        !this.isSpecificationAssignable(specification, validTypeIds)
                        || !WorkScheduleUtil.isDateRangeOverlapping({begin: specification.begin, end: specification.end}, beginDateTime, endDateTime)
                    ) {
                        invalidSpecificationIdsForThisDeployment.push(specification.id!);
                    }
                }

                if (invalidSpecificationIdsForThisDeployment.length === deployment.specifications!.length) {
                    invalidDeploymentIds.push(Number(deploymentId));
                } else {
                    invalidSpecificationIds.push(...invalidSpecificationIdsForThisDeployment);
                }
            }

            const groupedFunctionRequirements: PlanbordWorkSchedulePosition['groupedFunctionRequirements'] = workSchedulePosition.groupedFunctionRequirements ?? [];
            if (groupedFunctionRequirements.length > 0) {
                for (const mappedResource of Object.values(this.resourceFunctionIdsAndDeploymentIdsMap)) {
                    let hasFunctionRequirements: boolean = false;
                    for (const group of groupedFunctionRequirements) {
                        if (mappedResource.functionIds.length > 0 && group.every((functionRequirement: WorkSchedulePositionFunctionRequirement): boolean => {
                            return mappedResource.functionIds.includes(functionRequirement.functionOfResourceId);
                        })) {
                            hasFunctionRequirements = true;
                            break;
                        }
                    }

                    if (!hasFunctionRequirements) {
                        invalidDeploymentIds.push(...mappedResource.deploymentIds);
                    }
                }
            }

            this.specificationsNotAvailableForDrop = [...new Set(invalidSpecificationIds)];
            this.deploymentsNotAvailableForDrop = [...new Set(invalidDeploymentIds)];
        }

        public clearNotDroppableDeploymentsAndSpecifications(): void {
            this.specificationsNotAvailableForDrop = [];
            this.deploymentsNotAvailableForDrop = [];
        }

        public assignAutomatically(): void {
            this.isAutomaticAssigning = true;

            const assignDate: string = Dates.Formatter.formatDate(this.activeDay!.date);

            ApiClient.instance.automaticAssignWorkScheduleAssignments(this.roster.rosterId, this.activeDay!.date)
                .then((): void => {
                    this.createNotification({text: `Werkindelingen automatisch toegewezen op ${assignDate}`, type: 'success'});
                    this.loadPositions(this.renderedDateRange.begin!, Dates.Utils.endOfDay(this.renderedDateRange.end!), true, true);
                })
                /** @see GlobalErrorModal.globalErrorHandler() */
                // @ts-expect-error TS2339
                .catch(GlobalErrorModal.__vfdConstructor.globalErrorHandler)
                .finally((): void => {
                    this.isAutomaticAssigning = false;
                });
        }

        public openDeleteModal(): void {
            /** @see GenericConfirmationModal.openModal() */
            // @ts-expect-error TS2339
            GenericConfirmationModal.__vfdConstructor.openModal<'YES' | 'NO'>(
                'Toegewezen werkindelingen verwijderen',
                'Weet je zeker dat je alle toegewezen werkindelingen wilt verwijderen',
                [{
                    value: 'YES',
                    text: 'Ja, ik weet het zeker',
                    color: ButtonColorScheme.Orange,
                }, {
                    value: 'NO',
                    text: 'Annuleren',
                }],
                'NO',
            ).then((value: 'YES' | 'NO'): void => {
                if (value === 'YES') {
                    this.removeAssigned();
                }
            });
        }

        private removeAssigned(): void {
            this.isRemovingAssignments = true;

            const removeDate: string = Dates.Formatter.formatDate(this.activeDay!.date);
            ApiClient.instance.deleteWorkScheduleAssignments(this.workScheduleAssignmentIdsOnActiveDay).then((): void => {
                this.createNotification({text: `Alle toegewezen werkindelingen zijn verwijderd op ${removeDate}.`, type: 'success'});
                this.loadPositionsForDay(this.activeDay!.date, true);
            }).catch((error: ApiError): void => {
                this.createNotification({text: `Toegewezen werkindelingen konden niet verwijderd worden op ${removeDate}.`, type: 'error'});
                console.error(error);
            }).finally((): void => {
                this.isRemovingAssignments = false;
            });
        }

        private updateFirstVisibleDate(): void {
            const scrollLeftHours: number = this.deploymentsHolder.scrollLeft / WorkScheduleUtil.HOURS_CELL_WIDTH_IN_PX;
            this.firstVisibleDate = Dates.Utils.addHours(this.firstRosterDay, Math.floor(scrollLeftHours));
        }

        private updateActiveDay(): void {
            const visibleDateMidRange: Date = Dates.Utils.addHours(this.firstVisibleDate!, this.visibleRangeInHours / 2);
            if (!Dates.compare(this.activeDay!.date).isSameDay(visibleDateMidRange)) {
                this.activeDay = this.roster.days.find((rosterDay: RosterDay): boolean => Dates.compare(rosterDay.date).isSameDay(visibleDateMidRange)) ?? null;
            }
        }

        private loadPreviousOrNextDay(): void {
            const firstRosterDayIsFirstDayOfRenderedDateRange: boolean = Dates.compare(this.renderedDateRange.begin!).isSameDay(this.firstRosterDay);
            const previousThresholdDate: Date = Dates.Utils.addHours(this.firstVisibleDate!, this.visibleRangeInHours * -0.5);
            const nextThresholdDate: Date = Dates.Utils.addHours(this.firstVisibleDate!, this.visibleRangeInHours * 1.5);
            const lastRosterDayIsLastDayOfRenderedDateRange: boolean = Dates.compare(this.renderedDateRange.end!).isSameDay(this.lastRosterDay);

            if (!firstRosterDayIsFirstDayOfRenderedDateRange && previousThresholdDate < this.renderedDateRange.begin!) {
                const previousBeginDate: Date = Dates.Utils.addDays(this.renderedDateRange.begin!, -1);
                this.renderedDateRange = {
                    begin: previousBeginDate,
                    end: Dates.Utils.addDays(this.renderedDateRange.end!, -1),
                };
                this.loadPositionsForDay(previousBeginDate);
            } else if (!lastRosterDayIsLastDayOfRenderedDateRange && Dates.Utils.endOfDay(this.renderedDateRange.end!) < nextThresholdDate) {
                const nextEndDate: Date = Dates.Utils.addDays(this.renderedDateRange.end!, 1);
                this.renderedDateRange = {
                    begin: Dates.Utils.addDays(this.renderedDateRange.begin!, 1),
                    end: nextEndDate,
                };
                this.loadPositionsForDay(nextEndDate);
            }
        }

        public moveDivider(offset: number): void {
            if (this.workSchedulesHeight === 0) {
                this.workSchedulesHeight = this.workSchedulePositionsHolder!.clientHeight;
            }
            this.workSchedulesHeight += offset;
            this.checkDividerHeight();
        }

        public checkDividerHeight(): void {
            if (this.workSchedulesHeight <= this.DIVIDER_MIN_HEIGHT) {
                this.workSchedulesHeight = this.DIVIDER_MIN_HEIGHT;
            }

            if (this.workSchedulesHeight > (this.content!.clientHeight - this.DIVIDER_MIN_HEIGHT)) {
                this.workSchedulesHeight = (this.content!.clientHeight - this.DIVIDER_MIN_HEIGHT);
            }
        }

        public scrollHorizontal(): void {
            if (!this.hasActiveDay) {
                return;
            }

            this.syncScrollPositions();

            this.updateFirstVisibleDate();
            this.updateActiveDay();
            this.loadPreviousOrNextDay();
        }

        private syncScrollPositions(): void {
            this.workSchedulePositionsHolder.scrollLeft = this.deploymentsHolder.scrollLeft;
        }

        public closeModal(): void {
            for (const dateString in this.allWorkSchedulePositionsByDateAsFlatArray) {
                const workSchedulePositionsForDate: PlanbordWorkSchedulePosition[] = Object.values(this.allWorkSchedulePositionsByDateAsFlatArray[dateString]);
                if (workSchedulePositionsForDate.length === 0) {
                    continue;
                }
                const date: Date = new Date(dateString + 'T00:00:00');
                this.$emit(
                    'update:workSchedules',
                    Dates.Formatter.formatDateISO(date),
                    workSchedulePositionsForDate.filter((position: PlanbordWorkSchedulePosition): boolean => Dates.compare(position.beginDateTime).isSameDay(date)),
                );
            }

            this.show = false;
            this.activeDay = null;
            this.firstVisibleDate = null;
            this.renderedDateRange = {begin: null, end: null};
            this.$emit('close:workSchedule');
        }

        public static openModal(rosterDay: RosterDay): void {
            WorkSchedule.instance!.activeDay = rosterDay;
            WorkSchedule.instance!.renderedDateRange = {
                begin: Dates.Utils.max(Dates.Utils.addDays(rosterDay.date, -1), WorkSchedule.instance!.firstRosterDay),
                end: Dates.Utils.endOfDay(Dates.Utils.min(Dates.Utils.addDays(rosterDay.date, 1), WorkSchedule.instance!.lastRosterDay)),
            };
            WorkSchedule.instance!.show = true;
            WorkSchedule.instance!.$nextTick((): void => {
                WorkSchedule.instance!.setScrollPosition(rosterDay.date);
            });

            Promise.all([
                this.instance!.loadLabels(),
                this.instance!.loadWorkingDeploymentStates(),
                this.instance!.loadPositions(WorkSchedule.instance!.renderedDateRange.begin!, WorkSchedule.instance!.renderedDateRange.end!),
            ])
                .then((): void => {
                    this.instance!.resetWorkScheduleGroupLabelLoadedPositions();
                    this.instance!.resetWorkSchedulePreferencesTypeFilter();

                    // Half content height - boardDivider half height
                    const halfHeight: number = Math.floor(this.instance!.content!.clientHeight / 2) - 10;
                    this.instance!.workSchedulesHeight = this.instance!.workSchedulePositionsHolder!.scrollHeight >= halfHeight ? halfHeight : this.instance!.workSchedulePositionsHolder!.scrollHeight;
                });
        }
    }
</script>

<style lang="scss" scoped>
    .workScheduleModal {
        --td-bottom-border-shadow: inset 0 -6px 0;
        --main-background-color: var(--cq-card-background);

        .workSchedulePositionsHolder,
        .deploymentsHolder {
            overflow: scroll;
            padding-bottom: 0.5rem;

            .scrollButton {
                position: absolute;
                margin-top: 1.6rem;
                z-index: 101;

                &.scrollButtonLeft {
                    left: calc(var(--label-cell-width) - 1rem);
                }

                &.scrollButtonRight {
                    right: 0;
                }
            }

            .workScheduleGridContainer {
                grid-template-columns: 10rem 1fr;
                padding: 0;
                display: inline-grid;
                min-width: 100%;
            }
        }

        .workSchedulePositionsHolder {
            height: var(--work-schedules-height);
            overflow-x: hidden;
        }

        .extraHeaderContent {
            padding: 0 2rem 1rem 0;
            display: flex;
            gap: 1rem;
            align-items: center;
            justify-content: end;
        }

        .content {
            overflow: hidden;
            display: grid;
            grid-template-rows: auto 20px 1fr;
            height: calc(100vh - 2rem - 7rem - var(--cq-card-gradient-top-size));
            position: relative;
            z-index: 0;
        }
    }
</style>
